import React, { useState, useEffect, useCallback } from 'react';
import {
  ArrowLeft,
  Briefcase,
  FileText,
  Shield,
  Plus,
  Download,
  Loader,
  ChevronDown,
  ChevronRight,
  User,
  Mail,
  Send,
  UserX,
} from 'lucide-react';
import { useBreadcrumbs } from '../../contexts/BreadcrumbContext';
import { useTranslation } from '../../i18n/hooks/useTranslation';
import { useNavigation } from '../../contexts/NavigationContext';
import { ConfirmDialog } from '../common/ConfirmDialog';

interface RetentionTier {
  min_value: number;
  max_value?: number;
  percent: number;
}

function resolveRetentionPercent(
  tiers: RetentionTier[] | undefined,
  contractValue: number,
  defaultPercent: number
): number {
  if (!tiers || tiers.length === 0) return defaultPercent;
  const sorted = [...tiers].sort((a, b) => a.min_value - b.min_value);
  for (const t of sorted) {
    if (contractValue < t.min_value) continue;
    if (t.max_value != null && contractValue > t.max_value) continue;
    return t.percent;
  }
  return defaultPercent;
}

interface ConstructionProject {
  _id: string;
  project_ref: string;
  project_name: string;
  country_code: string;
  contract_value: number;
  start_date: string;
  forecast_practical_completion: string;
  defects_liability_months: number;
  retention_default_percent: number;
  retention_cap_percent: number;
  retention_tiers?: RetentionTier[];
  funding_structure?: {
    escrow_mobilisation: boolean;
    escrow_milestones: boolean;
    retention_enabled: boolean;
  };
  default_currency?: string;
  client_org_id?: { companyName: string };
  client_contact_id?: { _id: string; name: string; email?: string };
  client_contact_ids?: Array<{ _id: string; name: string; email?: string }>;
  main_contractor_org_id?: { companyName: string };
}

/** Format amount as $0,000,000 */
const formatMoney = (n: number | undefined | null) =>
  n != null ? `$${n.toLocaleString('en-US', { minimumFractionDigits: 0, maximumFractionDigits: 2 })}` : '-';
const parseMoney = (s: string) => parseFloat(s.replace(/[,$]/g, '')) || 0;

interface ConstructionContract {
  _id: string;
  contract_value: number;
  retention_percent: number;
  retention_cap_amount: number;
  pc_release_percent: number;
  final_release_percent: number;
  defects_liability_months: number;
  party_a_org_id?: { companyName: string };
  party_b_org_id?: { companyName: string };
}

interface EscrowAgreement {
  _id: string;
  type: string;
  schedule_type?: 'milestone' | 'claimed_percent';
  status: string;
  total_amount: number;
  currency: string;
  payer_org_id?: { companyName: string };
  payee_org_id?: { companyName: string };
  milestones?: EscrowMilestone[];
}

interface EscrowMilestone {
  _id: string;
  title: string;
  amount: number;
  percent?: number;
  due_date?: string;
  status: string;
  evidence_required: boolean;
}

interface RetentionSummary {
  total_required: number;
  total_held: number;
  shortfall: number;
}

interface Account {
  _id: string;
  companyName: string;
}

interface ProjectDetailProps {
  projectId: string;
  onBack: () => void;
}

function ClientPortalLinksSection({
  projectId,
  project,
}: {
  projectId: string;
  project: ConstructionProject | null;
}) {
  const [links, setLinks] = useState<{ _id: string; userId: { email: string; firstName?: string; lastName?: string } }[]>([]);
  const [emailInput, setEmailInput] = useState('');
  const [invitingEmail, setInvitingEmail] = useState<string | null>(null);
  const [cancellingLinkId, setCancellingLinkId] = useState<string | null>(null);
  const [cancelAccessLink, setCancelAccessLink] = useState<{ id: string; email: string } | null>(null);
  const [loading, setLoading] = useState(true);
  const [inviteError, setInviteError] = useState<string | null>(null);
  const apiUrl = process.env.REACT_APP_API_URL || 'http://localhost:5000';
  const token = localStorage.getItem('token');

  const fetchLinks = useCallback(async () => {
    if (!token) return;
    try {
      const res = await fetch(`${apiUrl}/api/construction/projects/${projectId}/client-links`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = await res.json();
      if (data.success) setLinks(data.data || []);
    } catch {
      setLinks([]);
    } finally {
      setLoading(false);
    }
  }, [apiUrl, projectId, token]);

  useEffect(() => {
    fetchLinks();
  }, [fetchLinks]);

  const invitedEmails = new Set(links.map((l) => ((l.userId as any)?.email || '').toLowerCase()));

  const projectClientContacts: Array<{ _id: string; name: string; email?: string }> = [];
  const ids = (project?.client_contact_ids as any) || [];
  if (ids.length > 0) {
    for (const c of ids) {
      const cc = c && (c._id || c.name || c.email) ? c : null;
      if (cc?.email) projectClientContacts.push({ _id: String(cc._id || cc), name: cc.name || 'Client', email: cc.email });
    }
  } else if (project?.client_contact_id && (project.client_contact_id as any)?._id) {
    const cc = project.client_contact_id as any;
    if (cc.email) projectClientContacts.push({ _id: String(cc._id), name: cc.name || 'Client', email: cc.email });
  }

  const inviteByEmail = async (email: string): Promise<boolean> => {
    const emailNorm = email.trim().toLowerCase();
    if (!emailNorm || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(emailNorm)) return false;
    if (!token || invitingEmail) return false;
    setInviteError(null);
    setInvitingEmail(emailNorm);
    try {
      const res = await fetch(`${apiUrl}/api/construction/projects/${projectId}/invite-client`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ email: emailNorm }),
      });
      const data = await res.json();
      if (data.success) {
        fetchLinks();
        return true;
      }
      setInviteError(data.message || 'Failed to invite client');
      return false;
    } catch {
      setInviteError('Failed to invite client');
      return false;
    } finally {
      setInvitingEmail(null);
    }
  };

  const inviteFromInput = async () => {
    const email = emailInput.trim().toLowerCase();
    if (!email || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      setInviteError('Please enter a valid email address');
      return;
    }
    const ok = await inviteByEmail(email);
    if (ok) setEmailInput('');
  };

  const reinviteByEmail = async (email: string) => {
    await inviteByEmail(email);
  };

  const confirmCancelAccess = () => {
    if (!cancelAccessLink || !token) return;
    setCancellingLinkId(cancelAccessLink.id);
    setCancelAccessLink(null);
    fetch(`${apiUrl}/api/construction/projects/${projectId}/client-links/${cancelAccessLink.id}`, {
      method: 'DELETE',
      headers: { Authorization: `Bearer ${token}` },
    })
      .then((res) => res.json())
      .then((data) => {
        if (data.success) fetchLinks();
      })
      .finally(() => setCancellingLinkId(null));
  };

  const requestCancelAccess = (linkId: string, email: string) => {
    setCancelAccessLink({ id: linkId, email });
  };

  return (
    <div className="bg-white dark:bg-gray-800 rounded-lg shadow overflow-hidden">
      <div className="px-6 py-4 border-b border-gray-200 dark:border-gray-700">
        <h2 className="text-lg font-semibold flex items-center gap-2">
          <User className="w-5 h-5 text-accent-600" />
          Client Portal Access
        </h2>
        <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
          Invite project clients to access this project. Add any email or select from your client contacts below.
        </p>
      </div>
      <div className="p-6 space-y-4">
        <div className="flex gap-2">
          <input
            type="email"
            value={emailInput}
            onChange={(e) => { setEmailInput(e.target.value); setInviteError(null); }}
            onKeyDown={(e) => e.key === 'Enter' && inviteFromInput()}
            placeholder="client@example.com"
            className="flex-1 px-3 py-2 border rounded dark:bg-gray-700 dark:border-gray-600"
          />
          <button
            onClick={inviteFromInput}
            disabled={!!invitingEmail || !emailInput.trim()}
            className="inline-flex items-center gap-2 px-4 py-2 bg-accent-600 text-white rounded hover:bg-accent-700 disabled:opacity-50 disabled:cursor-not-allowed"
            title="Invite to project"
          >
            {invitingEmail ? <Loader className="w-4 h-4 animate-spin" /> : <Mail className="w-4 h-4" />}
            Invite to project
          </button>
        </div>
        {inviteError && (
          <div className="text-sm text-red-600 dark:text-red-400">{inviteError}</div>
        )}
        {projectClientContacts.length > 0 && (
          <div>
            <div className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Project clients (ready to invite)</div>
            <div className="border rounded dark:border-gray-600 divide-y dark:divide-gray-600">
              {projectClientContacts
                .filter((c) => c.email && !invitedEmails.has(c.email.toLowerCase()))
                .map((c) => (
                  <div key={c._id} className="flex items-center justify-between px-4 py-2">
                    <span className="text-sm">
                      {c.name} – {c.email}
                    </span>
                    <button
                      onClick={() => inviteByEmail(c.email!)}
                      disabled={!!invitingEmail}
                      className="p-2 text-accent-600 hover:text-accent-700 hover:bg-accent-50 dark:hover:bg-accent-900/20 rounded disabled:opacity-50"
                      title="Invite"
                    >
                      {invitingEmail === c.email?.toLowerCase() ? <Loader className="w-4 h-4 animate-spin" /> : <Mail className="w-4 h-4" />}
                    </button>
                  </div>
                ))}
            </div>
          </div>
        )}
        {loading ? (
          <div className="text-sm text-gray-500">Loading...</div>
        ) : links.length === 0 ? (
          <div className="text-sm text-gray-500 dark:text-gray-400 py-2">
            No clients invited yet. Select clients on the project first, then add their emails above or invite from the list.
          </div>
        ) : (
          <ul className="space-y-2">
            <div className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Invited</div>
            {links.map((l) => {
              const email = (l.userId as any)?.email || '';
              const emailLower = email.toLowerCase();
              return (
                <li key={l._id} className="flex items-center justify-between py-2 border-b border-gray-100 dark:border-gray-700 last:border-0">
                  <span className="text-sm">{email}</span>
                  <div className="flex items-center gap-1">
                    <button
                      onClick={() => reinviteByEmail(email)}
                      disabled={!!invitingEmail}
                      className="p-2 text-accent-600 hover:text-accent-700 hover:bg-accent-50 dark:hover:bg-accent-900/20 rounded disabled:opacity-50"
                      title="Reinvite"
                    >
                      {invitingEmail === emailLower ? <Loader className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
                    </button>
                    <button
                      onClick={() => requestCancelAccess(l._id, email)}
                      disabled={!!cancellingLinkId}
                      className="p-2 text-red-600 hover:text-red-700 hover:bg-red-50 dark:hover:bg-red-900/20 rounded disabled:opacity-50"
                      title="Cancel access"
                    >
                      {cancellingLinkId === l._id ? <Loader className="w-4 h-4 animate-spin" /> : <UserX className="w-4 h-4" />}
                    </button>
                  </div>
                </li>
              );
            })}
          </ul>
        )}
      </div>
      <ConfirmDialog
        isOpen={!!cancelAccessLink}
        title="Cancel access"
        message={
          cancelAccessLink
            ? `Revoke ${cancelAccessLink.email}'s access to this project? They will need to be re-invited to access it again.`
            : ''
        }
        type="danger"
        confirmLabel="Revoke access"
        onConfirm={confirmCancelAccess}
        onCancel={() => setCancelAccessLink(null)}
      />
    </div>
  );
}

export const ProjectDetail: React.FC<ProjectDetailProps> = ({ projectId, onBack }) => {
  const { setCustomBreadcrumbs } = useBreadcrumbs();
  const { t } = useTranslation();
  const { navigate } = useNavigation();
  const [project, setProject] = useState<ConstructionProject | null>(null);
  const [contracts, setContracts] = useState<ConstructionContract[]>([]);
  const [escrowAgreements, setEscrowAgreements] = useState<EscrowAgreement[]>([]);
  const [retentionSummary, setRetentionSummary] = useState<RetentionSummary | null>(null);
  const [accounts, setAccounts] = useState<Account[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showCreateContract, setShowCreateContract] = useState(false);
  const [showCreateEscrow, setShowCreateEscrow] = useState(false);
  const [expandedEscrow, setExpandedEscrow] = useState<string | null>(null);
  const [showAddMilestone, setShowAddMilestone] = useState<string | null>(null);

  const apiUrl = process.env.REACT_APP_API_URL || 'http://localhost:5000';
  const token = localStorage.getItem('token');

  const fetchProject = useCallback(async () => {
    if (!token) return;
    const res = await fetch(`${apiUrl}/api/construction/projects/${projectId}`, {
      headers: { Authorization: `Bearer ${token}` },
    });
    const data = await res.json();
    if (data.success) setProject(data.data);
    else setError(data.message || 'Failed to load project');
  }, [apiUrl, projectId, token]);

  const fetchContracts = useCallback(async () => {
    if (!token) return;
    const res = await fetch(`${apiUrl}/api/construction/projects/${projectId}/contracts`, {
      headers: { Authorization: `Bearer ${token}` },
    });
    const data = await res.json();
    if (data.success) setContracts(data.data || []);
  }, [apiUrl, projectId, token]);

  const fetchEscrowAgreements = useCallback(async () => {
    if (!token) return;
    const res = await fetch(`${apiUrl}/api/construction/projects/${projectId}/escrow-agreements`, {
      headers: { Authorization: `Bearer ${token}` },
    });
    const data = await res.json();
    let agreements = data.data || [];
    for (const a of agreements) {
      const detailRes = await fetch(`${apiUrl}/api/construction/escrow-agreements/${a._id}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const detailData = await detailRes.json();
      if (detailData.success && detailData.data.milestones) {
        a.milestones = detailData.data.milestones;
      }
    }
    setEscrowAgreements(agreements);
  }, [apiUrl, projectId, token]);

  const fetchRetentionSummary = useCallback(async () => {
    if (!token) return;
    const res = await fetch(`${apiUrl}/api/construction/projects/${projectId}/retention/summary`, {
      headers: { Authorization: `Bearer ${token}` },
    });
    const data = await res.json();
    if (data.success) setRetentionSummary(data.data);
  }, [apiUrl, projectId, token]);

  const fetchAccounts = useCallback(async () => {
    if (!token) return;
    let res = await fetch(`${apiUrl}/api/accounts`, {
      headers: { Authorization: `Bearer ${token}` },
    });
    let data = await res.json();
    if (data.success && data.data?.length) {
      setAccounts(Array.isArray(data.data) ? data.data : [data.data] || []);
      return;
    }
    res = await fetch(`${apiUrl}/api/users/me/accounts`, {
      headers: { Authorization: `Bearer ${token}` },
    });
    data = await res.json();
    if (data.success && data.data?.length) {
      setAccounts(data.data.map((a: { accountId: string; companyName: string }) => ({
        _id: a.accountId,
        companyName: a.companyName,
      })));
    }
  }, [apiUrl, token]);

  useEffect(() => {
    setCustomBreadcrumbs([
      { label: t('navigation.home') || 'Home', path: '/', icon: 'Home' },
      { label: t('navigation.projects') || 'Projects', path: '/projects', icon: 'Briefcase' },
      { label: project?.project_ref || projectId, path: `/projects/${projectId}`, icon: 'FileText' },
    ]);
    return () => setCustomBreadcrumbs(null);
  }, [t, setCustomBreadcrumbs, projectId, project?.project_ref]);

  useEffect(() => {
    const load = async () => {
      setLoading(true);
      setError(null);
      try {
        await Promise.all([
          fetchProject(),
          fetchContracts(),
          fetchEscrowAgreements(),
          fetchRetentionSummary(),
          fetchAccounts(),
        ]);
      } catch {
        setError('Failed to load project data');
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [fetchProject, fetchContracts, fetchEscrowAgreements, fetchRetentionSummary, fetchAccounts]);

  const refreshAll = useCallback(() => {
    fetchProject();
    fetchContracts();
    fetchEscrowAgreements();
    fetchRetentionSummary();
  }, [fetchProject, fetchContracts, fetchEscrowAgreements, fetchRetentionSummary]);

  const downloadReport = async () => {
    if (!project) return;
    const response = await fetch(
      `${apiUrl}/api/construction/projects/${projectId}/reports/retention.pdf`,
      { headers: { Authorization: `Bearer ${token}` } }
    );
    if (response.ok) {
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `retention-${project.project_ref}.pdf`;
      a.click();
      window.URL.revokeObjectURL(url);
    }
  };

  if (loading || !project) {
    return (
      <div className="flex items-center justify-center p-12">
        <Loader className="w-8 h-8 animate-spin text-accent-600" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="p-6">
        <div className="p-4 bg-red-50 dark:bg-red-900/20 text-red-700 dark:text-red-400 rounded-lg">
          {error}
        </div>
        <button
          onClick={onBack}
          className="mt-4 inline-flex items-center text-accent-600 hover:text-accent-700"
        >
          <ArrowLeft className="w-4 h-4 mr-2" />
          Back to Projects
        </button>
      </div>
    );
  }

  return (
    <div className="p-6 space-y-6">
      <div className="flex items-center justify-between">
        <button
          onClick={onBack}
          className="inline-flex items-center text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white"
        >
          <ArrowLeft className="w-4 h-4 mr-2" />
          Back to Projects
        </button>
        <div className="flex items-center gap-2">
          <button
            onClick={() => navigate(`/projects/${projectId}/client-view`)}
            className="inline-flex items-center px-3 py-1.5 border border-gray-300 dark:border-gray-600 rounded text-sm hover:bg-gray-50 dark:hover:bg-gray-700"
          >
            <User className="w-4 h-4 mr-1" />
            View as client
          </button>
          <button
            onClick={downloadReport}
            className="inline-flex items-center px-3 py-1.5 border border-gray-300 dark:border-gray-600 rounded text-sm hover:bg-gray-50 dark:hover:bg-gray-700"
          >
            <Download className="w-4 h-4 mr-1" />
            Retention Report
          </button>
        </div>
      </div>

      <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-6">
        <div className="flex items-start gap-4">
          <div className="p-3 bg-accent-100 dark:bg-accent-900/30 rounded-lg">
            <Briefcase className="w-8 h-8 text-accent-600" />
          </div>
          <div className="flex-1">
            <h1 className="text-2xl font-semibold text-gray-900 dark:text-white">
              {project.project_ref} – {project.project_name}
            </h1>
            <div className="mt-2 flex flex-wrap gap-4 text-sm text-gray-500 dark:text-gray-400">
              <span>{project.country_code}</span>
              <span>Value: {formatMoney(project.contract_value)}</span>
              <span>
                Retention: {project.retention_tiers && project.retention_tiers.length > 0
                  ? `Tiered (${project.retention_tiers.length} tiers)`
                  : `${project.retention_default_percent}% (cap ${project.retention_cap_percent}%)`}
              </span>
              <span>DLP: {project.defects_liability_months} months</span>
            </div>
            {((project.client_contact_ids as any)?.length || project.client_contact_id || project.client_org_id) && (
              <p className="mt-1 text-sm">
                Client: {(project.client_contact_ids as any)?.length
                  ? (project.client_contact_ids as any).map((c: any) => c?.name).filter(Boolean).join(', ')
                  : project.client_contact_id?.name ?? project.client_org_id?.companyName}
                {project.main_contractor_org_id && ` • Contractor: ${project.main_contractor_org_id.companyName}`}
              </p>
            )}
          </div>
        </div>
      </div>

      {/* Retention Summary */}
      {retentionSummary && (
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-6">
          <h2 className="text-lg font-semibold mb-4 flex items-center gap-2">
            <Shield className="w-5 h-5 text-accent-600" />
            Retention Summary
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="p-4 bg-gray-50 dark:bg-gray-700/50 rounded-lg">
              <div className="text-sm text-gray-500 dark:text-gray-400">Required</div>
              <div className="text-xl font-semibold">{formatMoney(retentionSummary.total_required ?? 0)}</div>
            </div>
            <div className="p-4 bg-gray-50 dark:bg-gray-700/50 rounded-lg">
              <div className="text-sm text-gray-500 dark:text-gray-400">Held</div>
              <div className="text-xl font-semibold">{formatMoney(retentionSummary.total_held ?? 0)}</div>
            </div>
            <div className="p-4 bg-gray-50 dark:bg-gray-700/50 rounded-lg">
              <div className="text-sm text-gray-500 dark:text-gray-400">Shortfall</div>
              <div className={`text-xl font-semibold ${retentionSummary.shortfall > 0 ? 'text-amber-600' : ''}`}>
                {formatMoney(retentionSummary.shortfall ?? 0)}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Contracts */}
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow overflow-hidden">
        <div className="px-6 py-4 border-b border-gray-200 dark:border-gray-700 flex justify-between items-center">
          <h2 className="text-lg font-semibold flex items-center gap-2">
            <FileText className="w-5 h-5 text-accent-600" />
            Contracts
          </h2>
          <button
            onClick={() => setShowCreateContract(true)}
            className="inline-flex items-center px-3 py-1.5 bg-accent-600 text-white rounded text-sm hover:bg-accent-700"
          >
            <Plus className="w-4 h-4 mr-1" />
            Add Contract
          </button>
        </div>
        <div className="overflow-x-auto">
          {contracts.length === 0 ? (
            <div className="px-6 py-8 text-center text-gray-500 dark:text-gray-400">
              No contracts yet. Add a contract to define retention and payment terms.
            </div>
          ) : (
            <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
              <thead className="bg-gray-50 dark:bg-gray-700">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase">Parties</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase">Value</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase">Retention</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase">Release</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
                {contracts.map((c) => (
                  <tr key={c._id}>
                    <td className="px-6 py-4 text-sm">
                      {(c.party_a_org_id as any)?.companyName} / {(c.party_b_org_id as any)?.companyName}
                    </td>
                    <td className="px-6 py-4 text-sm">{formatMoney(c.contract_value)}</td>
                    <td className="px-6 py-4 text-sm">{c.retention_percent}% (cap {formatMoney(c.retention_cap_amount)})</td>
                    <td className="px-6 py-4 text-sm">PC {c.pc_release_percent}% / Final {c.final_release_percent}%</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>

      {/* Client Portal Access */}
      <ClientPortalLinksSection projectId={projectId} project={project} />

      {/* Escrow Agreements & Milestones */}
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow overflow-hidden">
        <div className="px-6 py-4 border-b border-gray-200 dark:border-gray-700 flex justify-between items-center">
          <h2 className="text-lg font-semibold flex items-center gap-2">
            <Shield className="w-5 h-5 text-accent-600" />
            Escrow Agreements & Milestones
          </h2>
          <button
            onClick={() => setShowCreateEscrow(true)}
            className="inline-flex items-center px-3 py-1.5 bg-accent-600 text-white rounded text-sm hover:bg-accent-700"
          >
            <Plus className="w-4 h-4 mr-1" />
            Add Escrow Agreement
          </button>
        </div>
        <div className="divide-y divide-gray-200 dark:divide-gray-700">
          {escrowAgreements.length === 0 ? (
            <div className="px-6 py-8 text-center text-gray-500 dark:text-gray-400">
              No escrow agreements yet. Create an escrow agreement to manage milestone payments.
            </div>
          ) : (
            escrowAgreements.map((agreement) => (
              <EscrowAgreementRow
                key={agreement._id}
                agreement={agreement}
                projectId={projectId}
                expanded={expandedEscrow === agreement._id}
                onToggle={() => setExpandedEscrow((e) => (e === agreement._id ? null : agreement._id))}
                showAddMilestone={showAddMilestone === agreement._id}
                onToggleAddMilestone={() => setShowAddMilestone((s) => (s === agreement._id ? null : agreement._id))}
                onRefresh={refreshAll}
              />
            ))
          )}
        </div>
      </div>

      {showCreateContract && (
        <CreateContractModal
          projectId={projectId}
          project={project}
          accounts={accounts}
          onClose={() => setShowCreateContract(false)}
          onSuccess={() => {
            setShowCreateContract(false);
            refreshAll();
          }}
        />
      )}

      {showCreateEscrow && (
        <CreateEscrowModal
          projectId={projectId}
          project={project}
          accounts={accounts}
          contracts={contracts}
          onClose={() => setShowCreateEscrow(false)}
          onSuccess={() => {
            setShowCreateEscrow(false);
            refreshAll();
          }}
        />
      )}
    </div>
  );
};

function EscrowAgreementRow({
  agreement,
  projectId,
  expanded,
  onToggle,
  showAddMilestone,
  onToggleAddMilestone,
  onRefresh,
}: {
  agreement: EscrowAgreement;
  projectId: string;
  expanded: boolean;
  onToggle: () => void;
  showAddMilestone: boolean;
  onToggleAddMilestone: () => void;
  onRefresh: () => void;
}) {
  const [submitting, setSubmitting] = useState(false);
  const apiUrl = process.env.REACT_APP_API_URL || 'http://localhost:5000';
  const token = localStorage.getItem('token');

  const handleAddMilestone = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;
    const title = (form.elements.namedItem('title') as HTMLInputElement).value;
    const amountEl = form.elements.namedItem('amount') as HTMLInputElement;
    const percentEl = form.elements.namedItem('percent') as HTMLInputElement;
    const dueDate = (form.elements.namedItem('due_date') as HTMLInputElement).value || undefined;
    const isClaimedPercent = (agreement.schedule_type || 'milestone') === 'claimed_percent';
    const amount = amountEl ? parseMoney(amountEl.value) : NaN;
    const percent = percentEl ? parseFloat(percentEl.value) : NaN;
    if (!title || (isClaimedPercent ? isNaN(percent) : isNaN(amount))) return;
    setSubmitting(true);
    try {
      const body: Record<string, unknown> = {
        title,
        due_date: dueDate || undefined,
        evidence_required: false,
        evidence_types: [],
      };
      if (isClaimedPercent) {
        body.percent = percent;
      } else {
        body.amount = amount;
      }
      const res = await fetch(`${apiUrl}/api/construction/escrow-agreements/${agreement._id}/milestones`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(body),
      });
      const data = await res.json();
      if (data.success) {
        onRefresh();
        onToggleAddMilestone();
      }
    } finally {
      setSubmitting(false);
    }
  };

  const payer = (agreement.payer_org_id as any)?.companyName || '—';
  const payee = (agreement.payee_org_id as any)?.companyName || '—';
  const milestones = agreement.milestones || [];

  return (
    <div>
      <div
        className="px-6 py-4 flex items-center justify-between cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-700/50"
        onClick={onToggle}
      >
        <div className="flex items-center gap-2">
          {expanded ? (
            <ChevronDown className="w-5 h-5 text-gray-500" />
          ) : (
            <ChevronRight className="w-5 h-5 text-gray-500" />
          )}
          <div>
            <div className="font-medium">
              {agreement.type} • {(agreement.schedule_type || 'milestone') === 'milestone' ? 'Milestones' : 'Claimed %'} • {agreement.status}
            </div>
            <div className="text-sm text-gray-500 dark:text-gray-400">
              {payer} → {payee} • {formatMoney(agreement.total_amount)}
            </div>
          </div>
        </div>
      </div>
      {expanded && (
        <div className="px-6 pb-4 bg-gray-50 dark:bg-gray-800/50">
          <div className="flex justify-between items-center mb-3">
            <span className="text-sm font-medium">{(agreement.schedule_type || 'milestone') === 'claimed_percent' ? 'Claims' : 'Milestones'}</span>
            <button
              onClick={(e) => {
                e.stopPropagation();
                onToggleAddMilestone();
              }}
              className="text-sm text-accent-600 hover:text-accent-700"
            >
              + Add {(agreement.schedule_type || 'milestone') === 'claimed_percent' ? 'Claim' : 'Milestone'}
            </button>
          </div>
          {showAddMilestone && (
            <form onSubmit={handleAddMilestone} className="mb-4 p-4 bg-white dark:bg-gray-800 rounded border">
              <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                <div>
                  <label className="block text-xs text-gray-500 dark:text-gray-400 mb-1">Name</label>
                  <input
                    name="title"
                    placeholder={(agreement.schedule_type || 'milestone') === 'claimed_percent' ? 'Claim name' : 'Milestone name'}
                    required
                    className="w-full px-3 py-2 border rounded dark:bg-gray-700 dark:border-gray-600"
                  />
                </div>
                {(agreement.schedule_type || 'milestone') === 'claimed_percent' ? (
                  <div>
                    <label className="block text-xs text-gray-500 dark:text-gray-400 mb-1">% of total</label>
                    <input
                      name="percent"
                      type="number"
                      step="0.1"
                      min="0"
                      max="100"
                      placeholder="0"
                      required
                      className="w-full px-3 py-2 border rounded dark:bg-gray-700 dark:border-gray-600"
                    />
                  </div>
                ) : (
                  <div>
                    <label className="block text-xs text-gray-500 dark:text-gray-400 mb-1">$ Amount</label>
                    <input
                      name="amount"
                      type="text"
                      placeholder="$0"
                      required
                      className="w-full px-3 py-2 border rounded dark:bg-gray-700 dark:border-gray-600"
                    />
                  </div>
                )}
                <div>
                  <label className="block text-xs text-gray-500 dark:text-gray-400 mb-1">Date</label>
                  <input
                    name="due_date"
                    type="date"
                    className="w-full px-3 py-2 border rounded dark:bg-gray-700 dark:border-gray-600"
                  />
                </div>
              </div>
              <div className="mt-2 flex gap-2">
                <button
                  type="submit"
                  disabled={submitting}
                  className="px-3 py-1.5 bg-accent-600 text-white rounded text-sm hover:bg-accent-700 disabled:opacity-50"
                >
                  {submitting ? 'Adding...' : 'Add'}
                </button>
                <button
                  type="button"
                  onClick={onToggleAddMilestone}
                  className="px-3 py-1.5 border rounded text-sm hover:bg-gray-100 dark:hover:bg-gray-700"
                >
                  Cancel
                </button>
              </div>
            </form>
          )}
          {milestones.length === 0 ? (
            <p className="text-sm text-gray-500 dark:text-gray-400">No milestones yet.</p>
          ) : (
            <ul className="space-y-2">
              {milestones.map((m) => (
                <li
                  key={m._id}
                  className="flex items-center justify-between py-2 px-3 bg-white dark:bg-gray-800 rounded border"
                >
                  <div>
                    <span className="font-medium">{m.title}</span>
                    {m.due_date && (
                      <span className="ml-2 text-sm text-gray-500">
                        Due: {new Date(m.due_date).toLocaleDateString()}
                      </span>
                    )}
                  </div>
                  <div className="flex items-center gap-2">
                    {(agreement.schedule_type || 'milestone') === 'claimed_percent' && m.percent != null ? (
                      <span className="font-semibold">{m.percent}% = {formatMoney(m.amount)}</span>
                    ) : (
                      <span className="font-semibold">{formatMoney(m.amount)} (100%)</span>
                    )}
                    <span className={`text-xs px-2 py-0.5 rounded ${m.status === 'released' ? 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400' : 'bg-gray-100 text-gray-700 dark:bg-gray-700 dark:text-gray-300'}`}>
                      {m.status}
                    </span>
                  </div>
                </li>
              ))}
            </ul>
          )}
        </div>
      )}
    </div>
  );
}

function CreateContractModal({
  projectId,
  project,
  accounts,
  onClose,
  onSuccess,
}: {
  projectId: string;
  project: ConstructionProject;
  accounts: Account[];
  onClose: () => void;
  onSuccess: () => void;
}) {
  const clientId = (project as any)?.client_org_id?._id ?? (project as any)?.client_org_id;
  const contractorId = (project as any)?.main_contractor_org_id?._id ?? (project as any)?.main_contractor_org_id;
  const cv = project?.contract_value || 0;
  const resolvedPercent = resolveRetentionPercent(
    project?.retention_tiers,
    cv,
    project?.retention_default_percent || 5
  );
  const [form, setForm] = useState({
    party_a_org_id: clientId || '',
    party_b_org_id: contractorId || '',
    contract_value: cv,
    retention_percent: resolvedPercent,
    retention_cap_amount: Math.round(cv * (resolvedPercent / 100)),
    pc_release_percent: 50,
    final_release_percent: 50,
    defects_liability_months: project?.defects_liability_months || 12,
  });
  const [submitting, setSubmitting] = useState(false);
  const hasTiers = project?.retention_tiers && project.retention_tiers.length > 0;

  const handleContractValueChange = (value: number) => {
    const pct = hasTiers
      ? resolveRetentionPercent(project!.retention_tiers!, value, project!.retention_default_percent || 5)
      : form.retention_percent;
    setForm((f) => ({
      ...f,
      contract_value: value,
      retention_percent: hasTiers ? pct : f.retention_percent,
      retention_cap_amount: Math.round(value * ((hasTiers ? pct : f.retention_percent) / 100)),
    }));
  };
  const apiUrl = process.env.REACT_APP_API_URL || 'http://localhost:5000';
  const token = localStorage.getItem('token');

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      const res = await fetch(`${apiUrl}/api/construction/projects/${projectId}/contracts`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(form),
      });
      const data = await res.json();
      if (data.success) onSuccess();
      else alert(data.message || 'Failed to create contract');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl max-w-md w-full max-h-[90vh] overflow-y-auto">
        <div className="p-6">
          <h3 className="text-lg font-semibold mb-4">Add Contract</h3>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label className="block text-sm font-medium mb-1">Party A (Client)</label>
              <select
                required
                value={form.party_a_org_id}
                onChange={(e) => setForm((f) => ({ ...f, party_a_org_id: e.target.value }))}
                className="w-full px-3 py-2 border rounded dark:bg-gray-700 dark:border-gray-600"
              >
                <option value="">Select...</option>
                {accounts.map((a) => (
                  <option key={a._id} value={a._id}>{a.companyName}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium mb-1">Party B (Contractor)</label>
              <select
                required
                value={form.party_b_org_id}
                onChange={(e) => setForm((f) => ({ ...f, party_b_org_id: e.target.value }))}
                className="w-full px-3 py-2 border rounded dark:bg-gray-700 dark:border-gray-600"
              >
                <option value="">Select...</option>
                {accounts.map((a) => (
                  <option key={a._id} value={a._id}>{a.companyName}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium mb-1">Contract Value</label>
              <input
                type="number"
                step="0.01"
                min="0"
                required
                value={form.contract_value}
                onChange={(e) => handleContractValueChange(parseFloat(e.target.value) || 0)}
                className="w-full px-3 py-2 border rounded dark:bg-gray-700 dark:border-gray-600"
              />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium mb-1">Retention %</label>
                <input
                  type="number"
                  step="0.1"
                  min="0"
                  max="100"
                  value={form.retention_percent}
                  onChange={(e) => {
                    const pct = parseFloat(e.target.value) || 0;
                    setForm((f) => ({
                      ...f,
                      retention_percent: pct,
                      retention_cap_amount: Math.round(f.contract_value * (pct / 100)),
                    }));
                  }}
                  className="w-full px-3 py-2 border rounded dark:bg-gray-700 dark:border-gray-600"
                />
                {hasTiers && (
                  <p className="mt-0.5 text-xs text-gray-500 dark:text-gray-400">
                    From tiered plan (based on contract value)
                  </p>
                )}
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">Retention Cap Amount</label>
                <input
                  type="text"
                  value={form.retention_cap_amount ? formatMoney(form.retention_cap_amount) : ''}
                  onChange={(e) => setForm((f) => ({ ...f, retention_cap_amount: parseMoney(e.target.value) }))}
                  placeholder="$0"
                  className="w-full px-3 py-2 border rounded dark:bg-gray-700 dark:border-gray-600"
                />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium mb-1">PC Release %</label>
                <input
                  type="number"
                  step="1"
                  min="0"
                  max="100"
                  value={form.pc_release_percent}
                  onChange={(e) => setForm((f) => ({ ...f, pc_release_percent: parseFloat(e.target.value) || 0 }))}
                  className="w-full px-3 py-2 border rounded dark:bg-gray-700 dark:border-gray-600"
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">Final Release %</label>
                <input
                  type="number"
                  step="1"
                  min="0"
                  max="100"
                  value={form.final_release_percent}
                  onChange={(e) => setForm((f) => ({ ...f, final_release_percent: parseFloat(e.target.value) || 0 }))}
                  className="w-full px-3 py-2 border rounded dark:bg-gray-700 dark:border-gray-600"
                />
              </div>
            </div>
            <div>
              <label className="block text-sm font-medium mb-1">Defects Liability (months)</label>
              <input
                type="number"
                min="0"
                value={form.defects_liability_months}
                onChange={(e) => setForm((f) => ({ ...f, defects_liability_months: parseInt(e.target.value) || 0 }))}
                className="w-full px-3 py-2 border rounded dark:bg-gray-700 dark:border-gray-600"
              />
            </div>
            <div className="flex justify-end gap-2 pt-4">
              <button type="button" onClick={onClose} className="px-4 py-2 border rounded hover:bg-gray-100 dark:hover:bg-gray-700">
                Cancel
              </button>
              <button type="submit" disabled={submitting} className="px-4 py-2 bg-accent-600 text-white rounded hover:bg-accent-700 disabled:opacity-50">
                {submitting ? 'Creating...' : 'Create'}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
}

function CreateEscrowModal({
  projectId,
  project,
  accounts,
  contracts,
  onClose,
  onSuccess,
}: {
  projectId: string;
  project: ConstructionProject;
  accounts: Account[];
  contracts: ConstructionContract[];
  onClose: () => void;
  onSuccess: () => void;
}) {
  const clientId = (project as any)?.client_org_id?._id ?? (project as any)?.client_org_id;
  const contractorId = (project as any)?.main_contractor_org_id?._id ?? (project as any)?.main_contractor_org_id;
  const [form, setForm] = useState({
    type: 'milestone' as 'job' | 'milestone' | 'hybrid',
    schedule_type: ((project as any)?.schedule_type || 'claimed_percent') as 'milestone' | 'claimed_percent',
    payer_org_id: clientId || '',
    payee_org_id: contractorId || '',
    project_id: projectId,
    contract_id: contracts[0]?._id || '',
    total_amount: project?.contract_value || 0,
    currency: project?.default_currency || 'NZD',
    country_code: project?.country_code || 'NZ',
  });
  const [submitting, setSubmitting] = useState(false);
  const apiUrl = process.env.REACT_APP_API_URL || 'http://localhost:5000';
  const token = localStorage.getItem('token');

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      const payload = { ...form };
      if (!payload.contract_id) delete (payload as any).contract_id;
      const res = await fetch(`${apiUrl}/api/construction/escrow-agreements`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(payload),
      });
      const data = await res.json();
      if (data.success) onSuccess();
      else alert(data.message || 'Failed to create escrow agreement');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl max-w-md w-full max-h-[90vh] overflow-y-auto">
        <div className="p-6">
          <h3 className="text-lg font-semibold mb-4">Add Escrow Agreement</h3>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label className="block text-sm font-medium mb-1">Schedule Type</label>
              <div className="flex rounded-lg border border-gray-300 dark:border-gray-600 overflow-hidden">
                <button
                  type="button"
                  onClick={() => setForm((f) => ({ ...f, schedule_type: 'milestone' }))}
                  className={`flex-1 px-4 py-2 text-sm font-medium ${
                    form.schedule_type === 'milestone'
                      ? 'bg-accent-600 text-white'
                      : 'bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-600'
                  }`}
                >
                  Milestones
                </button>
                <button
                  type="button"
                  onClick={() => setForm((f) => ({ ...f, schedule_type: 'claimed_percent' }))}
                  className={`flex-1 px-4 py-2 text-sm font-medium ${
                    form.schedule_type === 'claimed_percent'
                      ? 'bg-accent-600 text-white'
                      : 'bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-600'
                  }`}
                >
                  Claimed %
                </button>
              </div>
              <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                {form.schedule_type === 'milestone'
                  ? 'Fixed amounts – claim 100% per milestone when completed'
                  : 'Percent of total – claim a % of contract value (for larger projects)'}
              </p>
            </div>
            <div>
              <label className="block text-sm font-medium mb-1">Type</label>
              <select
                value={form.type}
                onChange={(e) => setForm((f) => ({ ...f, type: e.target.value as any }))}
                className="w-full px-3 py-2 border rounded dark:bg-gray-700 dark:border-gray-600"
              >
                <option value="job">Job</option>
                <option value="milestone">Milestone</option>
                <option value="hybrid">Hybrid</option>
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium mb-1">Payer</label>
              <select
                required
                value={form.payer_org_id}
                onChange={(e) => setForm((f) => ({ ...f, payer_org_id: e.target.value }))}
                className="w-full px-3 py-2 border rounded dark:bg-gray-700 dark:border-gray-600"
              >
                <option value="">Select...</option>
                {accounts.map((a) => (
                  <option key={a._id} value={a._id}>{a.companyName}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium mb-1">Payee</label>
              <select
                required
                value={form.payee_org_id}
                onChange={(e) => setForm((f) => ({ ...f, payee_org_id: e.target.value }))}
                className="w-full px-3 py-2 border rounded dark:bg-gray-700 dark:border-gray-600"
              >
                <option value="">Select...</option>
                {accounts.map((a) => (
                  <option key={a._id} value={a._id}>{a.companyName}</option>
                ))}
              </select>
            </div>
            {contracts.length > 0 && (
              <div>
                <label className="block text-sm font-medium mb-1">Contract (optional)</label>
                <select
                  value={form.contract_id}
                  onChange={(e) => setForm((f) => ({ ...f, contract_id: e.target.value }))}
                  className="w-full px-3 py-2 border rounded dark:bg-gray-700 dark:border-gray-600"
                >
                  <option value="">None</option>
                  {contracts.map((c) => (
                    <option key={c._id} value={c._id}>
                      {(c.party_a_org_id as any)?.companyName} / {(c.party_b_org_id as any)?.companyName}
                    </option>
                  ))}
                </select>
              </div>
            )}
            <div>
              <label className="block text-sm font-medium mb-1">Total Amount</label>
              <input
                type="text"
                required
                value={form.total_amount ? formatMoney(form.total_amount) : ''}
                onChange={(e) => setForm((f) => ({ ...f, total_amount: parseMoney(e.target.value) }))}
                placeholder="$0"
                className="w-full px-3 py-2 border rounded dark:bg-gray-700 dark:border-gray-600"
              />
            </div>
            <div className="flex justify-end gap-2 pt-4">
              <button type="button" onClick={onClose} className="px-4 py-2 border rounded hover:bg-gray-100 dark:hover:bg-gray-700">
                Cancel
              </button>
              <button type="submit" disabled={submitting} className="px-4 py-2 bg-accent-600 text-white rounded hover:bg-accent-700 disabled:opacity-50">
                {submitting ? 'Creating...' : 'Create'}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
}
